/**
 * Mapbox Directions API client.
 *
 * Why these request parameters matter:
 *  - `banner_instructions` returns the display banners, and crucially their
 *    `sub` banner carries the per-lane arrow data we draw in the lane bar.
 *  - `steps` + `geometries=geojson` gives us `intersections[].lanes`, the
 *    authoritative lane model (which lanes exist, which are valid for the turn).
 *  - `annotations=congestion` colours the route line by live traffic.
 *  - `bearings` biases the first leg to the direction the vehicle is actually
 *    pointing, which stops a reroute from proposing an immediate U-turn.
 *  - `max_height/width/weight` apply the Hino's dimensions so the route avoids
 *    low bridges and posted weight limits.
 */

import { MAPBOX_TOKEN } from '../core/config';
import { t } from '../core/i18n';
import type { LngLat, DirectionsResult, Route } from '../core/types';
import { activeVehicle, type Settings } from '../core/settings';

export class DirectionsError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = 'DirectionsError';
  }
}

export interface RouteRequest {
  origin: LngLat;
  destination: LngLat;
  /** Vehicle heading in degrees, used to bias the departure direction. */
  heading?: number | null;
  settings: Settings;
  signal?: AbortSignal;
  /** Extra road classes to exclude for this request only. */
  extraExclude?: string[];
  /** Force alternatives on/off for this request. */
  forceAlternatives?: boolean;
  /**
   * Optional intermediate waypoint - used by the fuel detour. Routing THROUGH
   * the station rather than TO it means the trip continues to the original
   * destination on its own once the waypoint is passed, so "return to the
   * normal route when we leave the PTT" needs no extra state to unwind.
   */
  /**
   * Intermediate waypoints. Always a list, never a bare point: `LngLat` is
   * itself a tuple, so `Array.isArray` cannot tell one point from many.
   */
  via?: LngLat[];
}

/** Human-readable messages for the API's error codes. */
const CODE_MESSAGES: Record<string, string> = {
  NoRoute: 'No drivable route found. The destination may be unreachable for this vehicle.',
  NoSegment: 'Could not find a road near the start or destination. Try moving the pin.',
  ProfileNotFound: 'That routing profile is unavailable.',
  InvalidInput: 'The route request was rejected as invalid.',
  Forbidden: 'Mapbox rejected the token. Check VITE_MAPBOX_TOKEN and its URL restrictions.',
};

function buildExcludes(s: Settings): string[] {
  const ex: string[] = [];
  if (s.route.avoidTolls) ex.push('toll');
  // With a transponder the queue is the cost, not the fee - so keep the toll
  // roads and drop only the plazas we would have to stop and pay cash at.
  else if (activeVehicle(s).easyPass) ex.push('cash_only_tolls');
  if (s.route.avoidMotorways) ex.push('motorway');
  if (s.route.avoidFerries) ex.push('ferry');
  if (s.route.avoidUnpaved) ex.push('unpaved');
  return ex;
}

function buildParams(req: RouteRequest, profile: string): URLSearchParams {
  const { settings } = req;
  const v = activeVehicle(settings);

  const p = new URLSearchParams({
    access_token: MAPBOX_TOKEN,
    geometries: 'geojson',
    overview: 'full',
    steps: 'true',
    banner_instructions: 'true',
    voice_instructions: 'true',
    voice_units: settings.units,
    roundabout_exits: 'true',
    // Thai instructions (banners + voice text) come straight from Mapbox.
    language: settings.language,
    alternatives: String(req.forceAlternatives ?? settings.route.alternatives),
  });

  // Congestion is only produced by the traffic profile; asking for it on plain
  // driving is rejected outright, so gate it.
  p.set(
    'annotations',
    profile === 'driving-traffic'
      ? 'congestion,duration,distance,speed,maxspeed'
      : 'duration,distance,speed,maxspeed',
  );

  const ex = [...new Set([...buildExcludes(settings), ...(req.extraExclude ?? [])])];
  if (ex.length) p.set('exclude', ex.join(','));

  // Vehicle dimensions. Only send them when they'd actually constrain routing -
  // sending a car's 1.95 m height is harmless but noisy, and for the truck these
  // are the whole point.
  if (v.kind === 'truck') {
    if (v.height) p.set('max_height', String(v.height));
    if (v.width) p.set('max_width', String(v.width));
    if (v.weight) p.set('max_weight', String(v.weight));
  }

  // Constrain departure to roughly the current heading (±60°). Every later
  // waypoint gets an empty entry, meaning "unconstrained" - the count must
  // match the number of coordinates or the API rejects the request.
  if (req.heading != null && Number.isFinite(req.heading)) {
    const rest = ';'.repeat((req.via?.length ?? 0) + 1);
    p.set('bearings', `${Math.round(req.heading)},60${rest}`);
  }

  return p;
}

async function request(req: RouteRequest, profile: string): Promise<DirectionsResult> {
  const { origin, destination } = req;
  const points = [origin, ...(req.via ?? []), destination];
  const coords = points.map((c) => `${c[0].toFixed(6)},${c[1].toFixed(6)}`).join(';');
  const url = `https://api.mapbox.com/directions/v5/mapbox/${profile}/${coords}?${buildParams(req, profile)}`;

  const res = await fetch(url, { signal: req.signal });
  let body: DirectionsResult & { message?: string };
  try {
    body = await res.json();
  } catch {
    throw new DirectionsError('Routing service returned an unreadable response.', 'BadResponse');
  }

  if (!res.ok || (body.code && body.code !== 'Ok')) {
    const code = body.code ?? (res.status === 401 || res.status === 403 ? 'Forbidden' : 'HttpError');
    throw new DirectionsError(CODE_MESSAGES[code] ?? body.message ?? `Routing failed (${code}).`, code);
  }
  if (!body.routes?.length) {
    throw new DirectionsError(CODE_MESSAGES.NoRoute, 'NoRoute');
  }
  return body;
}

/**
 * Fetch routes. Tries the live-traffic profile first when enabled and
 * transparently falls back to plain driving, because `driving-traffic` refuses
 * some parameter combinations and is rate-limited more aggressively.
 */
export async function fetchRoutes(req: RouteRequest): Promise<Route[]> {
  const wantTraffic = req.settings.route.useTraffic;
  const profiles = wantTraffic ? ['driving-traffic', 'driving'] : ['driving'];

  let lastError: unknown;
  for (const profile of profiles) {
    try {
      const result = await request(req, profile);
      return result.routes.map((r, i) => ({ ...r, _id: `${profile}-${i}-${Date.now()}` }));
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      lastError = err;
      // A genuine "no route exists" won't be fixed by changing profile.
      if (err instanceof DirectionsError && (err.code === 'NoRoute' || err.code === 'Forbidden')) {
        throw err;
      }
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new DirectionsError('Routing failed.', 'Unknown');
}

/**
 * Comparative route search: alongside the default (traffic-aware, with up to
 * two Mapbox alternatives), also query a toll-free and a motorway-free variant
 * in parallel, then dedupe and rank. This is what surfaces the "actually
 * better" choices - e.g. the free road only 4 minutes slower than the tollway.
 */
export async function fetchRouteAlternatives(
  req: RouteRequest,
  /**
   * Corridors this driver has actually used before, from nav/learnedRoutes.ts.
   * Routing through them surfaces local knowledge the engine cannot infer.
   */
  learned: LngLat[][] = [],
): Promise<Route[]> {
  const { settings } = req;
  const variants: Array<{ tag?: string; promise: Promise<Route[]> }> = [
    { promise: fetchRoutes({ ...req, forceAlternatives: settings.route.alternatives }) },
  ];

  for (const vias of learned.slice(0, 2)) {
    variants.push({
      tag: 'usual',
      promise: fetchRoutes({ ...req, via: vias, forceAlternatives: false }),
    });
  }
  if (settings.route.alternatives) {
    if (!settings.route.avoidTolls) {
      variants.push({
        tag: 'no-tolls',
        promise: fetchRoutes({ ...req, extraExclude: ['toll'], forceAlternatives: false }),
      });
    }
    if (!settings.route.avoidMotorways) {
      variants.push({
        tag: 'no-motorway',
        promise: fetchRoutes({ ...req, extraExclude: ['motorway'], forceAlternatives: false }),
      });
    }
  }

  const results = await Promise.allSettled(variants.map((v) => v.promise));
  const routes: Route[] = [];
  results.forEach((r, i) => {
    if (r.status !== 'fulfilled') return;
    for (const route of r.value) {
      routes.push(variants[i].tag ? { ...route, _tag: variants[i].tag } : route);
    }
  });

  // The primary request failing entirely is a real error worth surfacing.
  if (!routes.length) {
    const first = results[0];
    if (first.status === 'rejected') throw first.reason;
    throw new DirectionsError(CODE_MESSAGES.NoRoute, 'NoRoute');
  }

  // Dedupe near-identical routes (a toll-free variant often equals an
  // existing alternative): same distance within 1.5% and duration within 3%.
  const unique: Route[] = [];
  for (const route of routes) {
    const dup = unique.find(
      (u) =>
        Math.abs(u.distance - route.distance) / Math.max(u.distance, 1) < 0.015 &&
        Math.abs(u.duration - route.duration) / Math.max(u.duration, 1) < 0.03,
    );
    if (!dup) unique.push(route);
    else if (!dup._tag && route._tag) dup._tag = route._tag; // keep the informative label
  }

  unique.sort((a, b) => a.duration - b.duration);
  if (unique.length) unique[0]._tag = unique[0]._tag ?? 'fastest';
  return unique.slice(0, 4);
}

/** Label an alternative route for the route-choice sheet. */
export function summariseRoute(route: Route): string {
  const names = route.legs
    .flatMap((l) => l.summary.split(',').map((s) => s.trim()))
    .filter(Boolean);
  const unique = [...new Set(names)];
  return unique.slice(0, 2).join(' / ') || t('preview.alternative');
}
